import { useMutation, useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useRecoilState, useSetRecoilState } from 'recoil';

import clientLogo from '../../assets/icons/clients-small.svg';
import removeIcon from '../../assets/icons/trush-square.svg';
import DTHoodCleaning from '../../components/ui/clients/hood-cleaning/DTHoodCleaning';
import ClientFixedCard from '../../components/ui/clients/overall/ClientFixedCard';
import Breadcrumb from '../../components/ui/common/Breadcrumb';
import DropdownMultiple from '../../components/ui/common/DropdownMultiple';
import DateField from '../../components/ui/common/Inputs/DateField';
import Dropdown from '../../components/ui/common/Inputs/Dropdown';
import Toggle from '../../components/ui/common/Toggle';
import {
  HOOD_CLEANING_SERVICE_NAME,
  serviceFrequencyOptions,
} from '../../config/constants';
import { useClientSelected } from '../../hooks/useClientSelected';
import { useNotification } from '../../hooks/useNotification';
import { confirmationModalState } from '../../recoil/modalDelete';
import { selectedClient } from '../../recoil/navigation';
import { needRefreshState } from '../../recoil/needRefresh';
import { UPDATE_CLIENT } from '../../services/graphql/client/client-mutations';
import { GET_CLIENT_BY_ID } from '../../services/graphql/client/client-querys';
import {
  DELETE_SERVICE,
  UPDATE_SERVICE,
} from '../../services/graphql/service/service-mutations';
import type { ClientQuery } from '../../types/client';

const ClientHoodCleaningView = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const {
    notifySaveSuccess,
    notifySaveError,
    notifyDeleteOk,
    notifyDeleteError,
  } = useNotification();
  const { client } = useParams();

  const [serviceId, setServiceId] = useState<string>('');
  const setConfirmModalState = useSetRecoilState(confirmationModalState);
  const setNeedRefresh = useSetRecoilState(needRefreshState);
  const [clientSelected, setClientSelected] = useRecoilState(selectedClient);

  // === QUERIES ===

  const { data, refetch } = useQuery<ClientQuery>(GET_CLIENT_BY_ID, {
    variables: { clientId: client },
  });

  // This is necessary to load client info on reload on this page.
  // This hook needs to be on every single client page so the global state
  // knows what to show on the secondary menu for the client we currently are in.
  useClientSelected(data);

  // === MUTATIONS ===
  const [updateService] = useMutation<any>(UPDATE_SERVICE, {
    onError: () => {
      notifySaveError();
    },
    onCompleted: () => {
      notifySaveSuccess();
      refetch();
    },
  });

  const [updateClient] = useMutation<any>(UPDATE_CLIENT, {
    onError: () => {
      notifySaveError();
    },
    onCompleted: () => {
      notifySaveSuccess();
      refetch();
    },
  });

  const [deleteService] = useMutation<any>(DELETE_SERVICE, {
    variables: {
      serviceId,
    },
    onCompleted: () => {
      notifyDeleteOk();
      navigate(`/dashboard/clients/overall/${client}`);
    },
    onError: () => {
      notifyDeleteError();
    },
  });

  useEffect(() => {
    const service = data?.getClientById?.data.clientService.find(
      (_service) => _service.name === HOOD_CLEANING_SERVICE_NAME,
    );
    if (service) {
      setServiceId(service.id);
    }
  }, [data]);

  // === FUNCTIONS ===
  const handleFrequencyChange = (value: string) => {
    const service = data?.getClientById?.data.clientService.find(
      (_service) => _service.name === HOOD_CLEANING_SERVICE_NAME,
    );
    if (service) {
      updateService({
        variables: {
          serviceId: service.id,
          key: 'frequency',
          value,
        },
      });
    }
  };

  const handleReadyToVisitChange = (value: boolean) => {
    const service = data?.getClientById?.data.clientService.find(
      (_service) => _service.name === HOOD_CLEANING_SERVICE_NAME,
    );
    if (service) {
      updateService({
        variables: {
          serviceId: service.id,
          key: 'ready_to_visit',
          value: value.toString(),
        },
      });
    }
  };

  const handleLastServiceChange = (value: string) => {
    const service = data?.getClientById?.data.clientService.find(
      (_service) => _service.name === HOOD_CLEANING_SERVICE_NAME,
    );
    if (service) {
      updateService({
        variables: {
          serviceId: service.id,
          key: 'last_service',
          value,
        },
      });
    }
  };

  const handleNextScheduledChange = (value: string) => {
    const service = data?.getClientById?.data.clientService.find(
      (_service) => _service.name === HOOD_CLEANING_SERVICE_NAME,
    );
    if (service) {
      updateService({
        variables: {
          serviceId: service.id,
          key: 'next_scheduled',
          value,
        },
      });
    }
  };

  const handleAvaliableDaysChange = (value: string[]) => {
    const service = data?.getClientById?.data.clientService.find(
      (_service) => _service.name === HOOD_CLEANING_SERVICE_NAME,
    );
    if (service) {
      updateService({
        variables: {
          serviceId: service.id,
          key: 'available_days',
          value: value.toString().trim(),
        },
      });
    }
  };

  const handleRemoveService = () => {
    setConfirmModalState({
      isOpen: true,
      onConfirm: () => {
        deleteService();
        setClientSelected({
          ...clientSelected,
          services: clientSelected?.services?.filter(
            (service) => service !== HOOD_CLEANING_SERVICE_NAME,
          ),
        });
        setNeedRefresh(true);
        setTimeout(() => {
          navigate(`/dashboard/clients/overall/${client}`);
        }, 1000);
      },
      onCancel: () => {
        // Nothing happens
      },
      message: t('global.are-you-sure-delete'),
    });
  };

  return (
    <>
      <section>
        <div className="fixed top-0 z-20 w-full bg-white">
          <Breadcrumb
            title={`${t('clients.title-h1')}`}
            secondTitle={data?.getClientById?.data.client.name}
            thirdTitle={t('client-hood-cleaning.service-name')}
            image={clientLogo}
          />
        </div>
        <div className="mt-[30px] flex w-full flex-col md:flex-row">
          {/* Client Business Card */}
          <div className="flex w-[260px]">
            {data && client && (
              <ClientFixedCard
                client={client}
                data={data}
                updateClient={updateClient}
              />
            )}
          </div>

          <div className="flex w-full flex-col px-6 pt-6">
            {/* Service info */}
            <div className="w-full">
              <h1 className="mb-2 text-xl font-semibold">
                {t('client-hood-cleaning.service-info')}
              </h1>
              <div className="grid w-full max-w-6xl grid-cols-1 gap-2 md:grid-cols-2 md:gap-2 lg:grid-cols-3 lg:gap-4 xl:grid-cols-4 xl:gap-6">
                {/* Frequency option */}
                <div className="min-h-[44px] w-[200px]">
                  <Dropdown
                    options={serviceFrequencyOptions}
                    selectedValue={
                      data?.getClientById?.data.clientService.find(
                        (service) =>
                          service.name === HOOD_CLEANING_SERVICE_NAME,
                      )?.frequency || ''
                    }
                    onSelectChange={handleFrequencyChange}
                    label={t('client-hood-cleaning.frequency')}
                  />
                </div>

                {/* Last Service option */}
                <div className="min-h-[44px] w-[200px]">
                  <DateField
                    placeholder={t('global.select-date')}
                    onSelectChange={handleLastServiceChange}
                    selectedValue={
                      data?.getClientById?.data.clientService.find(
                        (service) =>
                          service.name === HOOD_CLEANING_SERVICE_NAME,
                      )?.last_service || ''
                    }
                    label={t('client-hood-cleaning.last-service')}
                    disabled
                  />
                </div>

                {/* Available days option */}
                <div className="min-h-[44px] w-[200px]">
                  <DropdownMultiple
                    label={t('global.available-days')}
                    placeholder={t('global.select-days')}
                    options={[
                      'Monday',
                      'Tuesday',
                      'Wednesday',
                      'Thursday',
                      'Friday',
                      'Saturday',
                      'Sunday',
                    ]}
                    onChange={handleAvaliableDaysChange}
                    selectedValues={
                      data?.getClientById?.data.clientService
                        .find(
                          (service) =>
                            service.name === HOOD_CLEANING_SERVICE_NAME,
                        )
                        ?.available_days?.filter((v) => v) || []
                    }
                  />
                </div>

                {/* Next Scheduled option */}
                <div className="min-h-[44px] w-[200px]">
                  <DateField
                    placeholder={t('global.select-date')}
                    onSelectChange={handleNextScheduledChange}
                    selectedValue={
                      data?.getClientById?.data.clientService.find(
                        (service) =>
                          service.name === HOOD_CLEANING_SERVICE_NAME,
                      )?.next_scheduled || ''
                    }
                    label={t('client-hood-cleaning.next-scheduled')}
                    disabled
                  />
                </div>

                {/* Ready to visit option */}
                <div className="min-h-[44px] w-[200px]">
                  <Toggle
                    selectedValue={
                      data?.getClientById?.data.clientService.find(
                        (service) =>
                          service.name === HOOD_CLEANING_SERVICE_NAME,
                      )?.ready_to_visit || false
                    }
                    onSelectChange={handleReadyToVisitChange}
                    label={t('client-hood-cleaning.ready-to-visit')}
                    placeholder={t('client-hood-cleaning.is-ready-to-visit')}
                  />
                </div>
              </div>
            </div>

            {/* Data table */}

            <h1 className="mb-2 mt-12 text-xl font-semibold">
              {t('client-hood-cleaning.table-label')}
            </h1>

            {data?.getClientById?.data.clientService && (
              <DTHoodCleaning
                serviceId={
                  data.getClientById.data.clientService.find(
                    (_service) => _service.name === HOOD_CLEANING_SERVICE_NAME,
                  )?.id || ''
                }
              />
            )}

            {/* Metrics */}

            {/* Delete service */}
            <div className="m-6 flex justify-center">
              <button
                onClick={handleRemoveService}
                className="flex items-center"
              >
                <img src={removeIcon} alt="remove icon" />
                <span className="ml-2 text-xs font-bold text-redish underline">
                  {t('client-hood-cleaning.remove-service')}
                </span>
              </button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ClientHoodCleaningView;
